import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { helpApi } from '../lib/api'
import type { HelpGapSubjectRow, HelpGapTypeRow, HelpKnowledgeGapsFilters } from '../lib/api'
import { apiErrorStatus } from '../hooks/useErrorHandler'
import { PageHeader, SurfaceCard } from '../components/ui'

export const HELP_GAPS_FORBIDDEN_MESSAGE = "You don't have access to knowledge gaps."

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export function gapRangeMessage(from: string, to: string): string | null {
  if (from === '' || to === '') return null
  if (!ISO_DATE.test(from) || !ISO_DATE.test(to)) return null
  if (to < from) return 'Choose an end date on or after the start date.'

  const start = Date.parse(`${from}T00:00:00Z`)
  const end = Date.parse(`${to}T00:00:00Z`) + 86_400_000
  const spanDays = (end - 1000 - start) / 86_400_000
  if (spanDays > 366) return 'Choose a range of 366 days or less.'

  return null
}

const ALL_STATUSES = ['open', 'in_progress', 'resolved', 'closed'] as const

export function gapListFilters(search: URLSearchParams) {
  const createdFrom = search.get('created_from') ?? ''
  const createdTo = search.get('created_to') ?? ''
  const ticketTypeId = search.get('ticket_type_id') ?? ''
  const subject = search.get('subject') ?? ''

  return {
    isGapLink: createdFrom !== '' || createdTo !== '' || ticketTypeId !== '' || subject !== '',
    createdFrom,
    createdTo,
    ticketTypeId,
    subject,
    status: [...ALL_STATUSES],
  }
}

function formatGapDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate)
  if (!match) return isoDate
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—'
  const minutes = Math.round(Math.abs(seconds) / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours}h`
  return `${Math.round(hours / 24)}d`
}

function GapTable({
  rows,
  empty,
}: {
  rows: Array<HelpGapTypeRow | HelpGapSubjectRow>
  empty: string
}) {
  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-500">{empty}</p>
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead>
          <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Tickets</th>
            <th className="px-3 py-2">Median time to resolve</th>
            <th className="px-3 py-2">Article</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => {
            const key = 'ticket_type_id' in row ? (row.ticket_type_id ?? 'none') : row.subject_key
            return (
              <tr key={key}>
                <td className="px-3 py-3 font-medium text-slate-900">{row.name}</td>
                <td className="px-3 py-3">
                  <Link to={row.tickets_url} className="font-semibold text-blue-700 hover:text-blue-900" aria-label={`${row.ticket_count} tickets`}>
                    {row.ticket_count}
                  </Link>
                </td>
                <td className="px-3 py-3 text-slate-700">
                  {formatDuration(row.median_resolve_seconds)}
                  <span className="ml-2 text-xs text-slate-500">
                    {row.resolve_samples === 1 ? '1 resolved' : `${row.resolve_samples} resolved`}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <Link to={row.write_article_url} className="font-semibold text-blue-700 hover:text-blue-900">
                    Write article
                  </Link>
                  {row.article_category ? (
                    <div className="mt-1 text-xs text-slate-500">{row.article_category.name}</div>
                  ) : null}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function HelpKnowledgeGapsPage() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const rangeError = gapRangeMessage(from, to)
  const rangeReady = from !== '' && to !== '' && rangeError === null
  const filters: HelpKnowledgeGapsFilters = rangeReady ? { from, to } : {}
  const gaps = useQuery({
    queryKey: ['help-gaps', filters],
    queryFn: () => helpApi.gaps(filters),
    enabled: rangeError === null,
    placeholderData: (previous) => previous,
  })

  const forbidden = gaps.isError && apiErrorStatus(gaps.error) === 403
  const data = gaps.data

  return (
    <div className="space-y-6">
      <PageHeader
        title="Knowledge gaps"
        subtitle="Ticket types and recurring subjects with no published Help hub article in the selected dates."
        action={<Link to="/help" className="text-sm font-medium text-blue-700 hover:text-blue-900">Back to Help</Link>}
      />

      <SurfaceCard padding="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm font-medium text-slate-700">
            From
            <input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm font-medium text-slate-700">
            To
            <input
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm"
            />
          </label>
        </div>
        {data ? (
          <p className="mt-3 text-sm text-slate-600">
            {formatGapDate(data.filters.from)} through {formatGapDate(data.filters.to)}. Ranked by ticket count, then time to resolve.
            A subject needs at least {data.subject_minimum} tickets. Showing up to {data.limit} rows in each list.
          </p>
        ) : null}
      </SurfaceCard>

      {gaps.isLoading ? <p className="text-sm text-slate-500" aria-live="polite">Loading knowledge gaps...</p> : null}
      {rangeError ? <p className="text-sm text-rose-700" role="alert">{rangeError}</p> : null}
      {forbidden ? <p className="text-sm text-rose-700" role="alert">{HELP_GAPS_FORBIDDEN_MESSAGE}</p> : null}
      {gaps.isError && !forbidden && !rangeError ? <p className="text-sm text-rose-700" role="alert">Could not load knowledge gaps.</p> : null}

      {data ? (
        <>
          <SurfaceCard>
            <h2 className="text-base font-semibold text-slate-950">Ticket types</h2>
            <div className="mt-4">
              <GapTable
                rows={data.ticket_types}
                empty="No ticket types in this range are missing a published article."
              />
            </div>
          </SurfaceCard>
          <SurfaceCard>
            <h2 className="text-base font-semibold text-slate-950">Recurring subjects</h2>
            <div className="mt-4">
              <GapTable
                rows={data.subjects}
                empty="No recurring subjects in this range are missing a published article."
              />
            </div>
          </SurfaceCard>
        </>
      ) : null}
    </div>
  )
}
